import { BasicConnectionRenderer } from "./basic.connection.renderer";
import { ConnectionLineTypes } from "flavr-common";
import { FlowConnectionRenderer } from "./flow.connection.renderer";
import { ArrowConnectionRenderer } from "./arrow.connection.renderer";
import { FacetRenderer } from "../renderable/Renderable";
import { modelPool } from "@modules/model";
function getOffsetRadius(scale) {
    return Math.min(scale.x, scale.y, scale.z) / 4;
}
function updateConnectionTransformations(connections, angleOffset, offsetRadius1, offsetRadius2) {
    let angle = 0.5 * angleOffset;
    for (const c of connections.values()) {
        c.updateTransform(angle, offsetRadius1, offsetRadius2);
        angle += angleOffset;
    }
}
function destroyConnections(connections) {
    for (const c of connections.values()) {
        c.destroy();
    }
}
/**
 * Visible Lines that connect Models
 */
export class Connections extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        this.in = new Map();
        this.out = new Map();
        this.connectedModelsDirty = true;
        this.connectedModels = new Set();
    }
    destroy() {
        for (const cs of this.in.values()) {
            destroyConnections(cs);
        }
        this.in.clear();
        for (const cs of this.out.values()) {
            destroyConnections(cs);
        }
        this.out.clear();
    }
    setVisible(visible) {
        for (const connections of this.in.values()) {
            for (const c of connections.values()) {
                c.setVisible(visible && c.from.visible);
            }
        }
        for (const connections of this.out.values()) {
            for (const c of connections.values()) {
                c.setVisible(visible && c.to.visible);
            }
        }
    }
    updateFacet(facet) {
        var _a, _b;
        for (const [target, connections] of this.out) {
            if (!facet.connections[target]) {
                // we no longer have any connection to 'target' so we have to destroy existing ones
                destroyConnections(connections);
            }
        }
        const roomUID = modelPool.getModelOrThrow(this.owner.modelUID).getRoomOrThrow().getUID();
        for (const targetUidStr in facet.connections) {
            const targetUid = Number(targetUidStr);
            const target = this.owner.renderer.getModel(targetUid);
            const targetRoomUID = (target !== undefined)
                ? (_b = (_a = modelPool.getModel(target.modelUID)) === null || _a === void 0 ? void 0 : _a.getRoom()) === null || _b === void 0 ? void 0 : _b.getUID()
                : undefined;
            if (target === undefined || targetRoomUID !== roomUID)
                continue;
            const outCon = this.out.get(targetUid);
            this.updateConnectionsToTarget(target, targetUid, facet.connections[targetUid], outCon);
        }
        this.connectedModelsDirty = true;
    }
    updateConnectionsToTarget(target, targetUid, connectionParams, existingConnections) {
        if (existingConnections !== undefined) {
            for (const [name, c] of existingConnections) {
                if (!connectionParams[name]) {
                    // connection no longer exists
                    c.destroy();
                    existingConnections.delete(name);
                }
            }
        }
        for (const name in connectionParams) {
            const params = connectionParams[name];
            const existing = existingConnections ? existingConnections.get(name) : undefined;
            if (existing) {
                if (!existing.update(params)) {
                    // could not update the exisiting connection
                    // probably because the type of the connection changed
                    // -> we have to recreate the connection
                    existing.destroy();
                    this.createConnection(target, targetUid, name, params);
                }
            }
            else {
                this.createConnection(target, targetUid, name, params);
            }
        }
        if (this.owner.matrixWorldValid && target.matrixWorldValid) {
            this.updateConnectionTransforms(target);
        }
    }
    createConnection(target, targetUid, connectionName, params) {
        var _a;
        const targetConnections = (target.connections = (_a = target.connections) !== null && _a !== void 0 ? _a : new Connections(target));
        targetConnections.connectedModelsDirty = true;
        let connection;
        switch (params.type) {
            case ConnectionLineTypes.Basic:
                connection = new BasicConnectionRenderer(connectionName, this.owner, target, params);
                break;
            case ConnectionLineTypes.Flow:
                connection = new FlowConnectionRenderer(connectionName, this.owner, target, params);
                break;
            case ConnectionLineTypes.Arrow:
                connection = new ArrowConnectionRenderer(connectionName, this.owner, target, params);
                break;
        }
        if (connection) {
            let conOut = this.out.get(targetUid);
            if (conOut === undefined) {
                this.out.set(targetUid, (conOut = new Map()));
            }
            conOut.set(connectionName, connection);
            const thisUid = this.owner.modelUID;
            let conIn = targetConnections.in.get(thisUid);
            if (!conIn) {
                targetConnections.in.set(thisUid, (conIn = new Map()));
            }
            conIn.set(connectionName, connection);
        }
    }
    updateTransform() {
        if (this.connectedModelsDirty) {
            this.connectedModelsDirty = false;
            this.connectedModels.clear();
            for (const uid of this.in.keys()) {
                const model = this.owner.renderer.getModel(uid);
                if (model)
                    this.connectedModels.add(model);
            }
            for (const uid of this.out.keys()) {
                const model = this.owner.renderer.getModel(uid);
                if (model)
                    this.connectedModels.add(model);
            }
        }
        this.connectedModels.forEach((model) => {
            if (model.matrixWorldValid) {
                this.updateConnectionTransforms(model);
            }
        });
    }
    updateConnectionTransforms(otherModel) {
        const otherUid = otherModel.modelUID;
        const connectionsTo = this.out.get(otherUid);
        const connectionsFrom = this.in.get(otherUid);
        const numConnections = (connectionsTo ? connectionsTo.size : 0) + (connectionsFrom ? connectionsFrom.size : 0);
        const radius1 = numConnections <= 1 ? 0 : getOffsetRadius(this.owner.worldScale);
        const radius2 = numConnections <= 1 ? 0 : getOffsetRadius(otherModel.worldScale);
        const angleOffset = (2 * Math.PI) / numConnections;
        connectionsTo && updateConnectionTransformations(connectionsTo, angleOffset, radius1, radius2);
        connectionsFrom && updateConnectionTransformations(connectionsFrom, angleOffset, radius2, radius1);
    }
}
